import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Link,
  List,
  ListItem,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerCloseButton,
  IconButton,
  useDisclosure,
  Divider,
} from "@chakra-ui/react";

/**
 * Termeni și condiții — Card Cadou Travel Time
 *
 * Necesită un ChakraProvider undeva mai sus în arbore (App.jsx / main.jsx),
 * de exemplu:
 *
 *   import { ChakraProvider } from "@chakra-ui/react";
 *   <ChakraProvider><TravelTimeTermeni /></ChakraProvider>
 *
 * Fonturile (Fraunces / IBM Plex Sans / IBM Plex Mono) sunt încărcate automat
 * din Google Fonts la montare. Pentru producție e mai bine să le adaugi direct
 * în <head>-ul aplicației, dar merge și așa pentru un demo/pagină standalone.
 */

const FONT_HEADING = "'Fraunces', Georgia, serif";
const FONT_BODY = "'IBM Plex Sans', system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";

const COLORS = {
  bg: "#F7F4EC",
  panel: "#FBFAF5",
  ink: "#1C2624",
  inkMuted: "#5B6664",
  teal: "#0E4749",
  tealDark: "#0A3436",
  gold: "#A6812C",
  hairline: "#DAD4C3",
};

const SECTIONS = [
  {
    id: "organizatorul",
    number: "1",
    title: "Organizatorul campaniei",
    paragraphs: [
      {
        num: "1.1",
        text: "Campania promoțională este organizată de TRAVEL TIME D&R SRL, denumită în continuare „Travel Time” sau „Organizatorul”, în colaborare cu partenerii participanți la campanie.",
      },
      {
        num: "1.2",
        text: "Campania se desfășoară în condițiile și pe durata comunicate de Travel Time prin intermediul cardului cadou, al materialelor promoționale sau al comunicărilor transmise beneficiarilor și partenerilor.",
      },
    ],
  },
  {
    id: "obiectul",
    number: "2",
    title: "Obiectul campaniei",
    paragraphs: [
      {
        num: "2.1",
        text: "În cadrul campaniei, Travel Time oferă beneficiarilor eligibili carduri cadou promoționale, a căror valoare este înscrisă pe fiecare card sau menționată în comunicarea prin care acesta este transmis.",
      },
      {
        num: "2.2",
        text: "Valoarea cardului cadou poate diferi în funcție de campanie, partener, beneficiar sau condițiile comerciale aplicabile.",
      },
      {
        num: "2.3",
        text: "Cardul cadou reprezintă un beneficiu comercial acordat în cadrul campaniei și poate fi utilizat exclusiv pentru achiziționarea unui pachet turistic eligibil comercializat prin Travel Time.",
      },
      {
        num: "2.4",
        text: "Cardul cadou nu reprezintă un card bancar sau un instrument de plată și nu poate fi utilizat în afara Travel Time.",
      },
    ],
  },
  {
    id: "beneficiarii",
    number: "3",
    title: "Beneficiarii campaniei",
    paragraphs: [
      {
        num: "3.1",
        text: "Cardul cadou este oferit exclusiv persoanelor recomandate de partenerii participanți la campanie sau persoanelor declarate eligibile de către Travel Time.",
      },
      {
        num: "3.2",
        text: "Acordarea și utilizarea cardului sunt condiționate de existența unui cod unic valid și, după caz, de confirmarea recomandării de către partenerul participant.",
      },
      {
        num: "3.3",
        text: "Travel Time își rezervă dreptul de a verifica eligibilitatea beneficiarului înainte de confirmarea utilizării cardului.",
      },
    ],
  },
  {
    id: "servicii",
    number: "4",
    title: "Serviciile pentru care poate fi utilizat cardul",
    paragraphs: [
      {
        num: "4.1",
        text: "Cardul poate fi utilizat exclusiv pentru rezervarea unui pachet turistic comercializat prin Travel Time, care poate include, în funcție de oferta aleasă, servicii precum cazare, transport, transferuri, masă sau alte servicii menționate în oferta contractuală.",
      },
      {
        num: "4.2",
        text: "Biletele de avion achiziționate separat nu fac obiectul campaniei și nu pot fi achitate cu ajutorul cardului cadou.",
      },
      {
        num: "4.3",
        text: "Cardul nu poate fi utilizat pentru servicii individuale, taxe, penalități, diferențe de tarif, modificări, anulări, asigurări sau alte servicii suplimentare, cu excepția situațiilor confirmate în scris de Travel Time.",
      },
      {
        num: "4.4",
        text: "Eligibilitatea unei oferte turistice pentru utilizarea cardului va fi confirmată de Travel Time înainte de efectuarea rezervării.",
      },
    ],
  },
  {
    id: "valoarea-minima",
    number: "5",
    title: "Valoarea minimă a rezervării",
    paragraphs: [
      {
        num: "5.1",
        text: "Cardul poate fi utilizat numai pentru o rezervare nouă care îndeplinește valoarea minimă comunicată pentru cardul respectiv, dacă o asemenea condiție este aplicabilă.",
      },
      {
        num: "5.2",
        text: "Valoarea minimă a rezervării poate fi înscrisă pe card sau menționată în comunicarea oficială prin care cardul este transmis beneficiarului.",
      },
      {
        num: "5.3",
        text: "Dacă este prevăzută o valoare minimă a rezervării, aceasta se calculează înainte de scăderea valorii cardului cadou.",
      },
      {
        num: "5.4",
        text: "În cazul în care serviciile sunt facturate în lei, valoarea cardului și, după caz, valoarea minimă a rezervării vor fi calculate în echivalent lei, potrivit cursului de schimb comunicat și utilizat de Travel Time la data rezervării.",
      },
      {
        num: "5.5",
        text: "Dacă valoarea rezervării scade ulterior sub pragul minim aplicabil, ca urmare a anulării unor servicii, a reducerii numărului de călători sau a modificării rezervării, Travel Time poate retrage beneficiul acordat și recalcula suma datorată.",
      },
    ],
  },
  {
    id: "modalitatea",
    number: "6",
    title: "Modalitatea de utilizare",
    paragraphs: [
      {
        num: "6.1",
        text: "Cardul poate fi utilizat o singură dată, integral, pentru o singură rezervare nouă.",
      },
      {
        num: "6.2",
        text: "Beneficiarul trebuie să comunice codul unic al cardului înainte de confirmarea și plata rezervării.",
      },
      {
        num: "6.3",
        text: "Cardul nu poate fi aplicat retroactiv unor rezervări deja efectuate, confirmate sau achitate integral ori parțial.",
      },
      {
        num: "6.4",
        text: "Valoarea cardului se deduce din prețul total al rezervării eligibile, după verificarea și confirmarea validității acestuia de către Travel Time.",
      },
      {
        num: "6.5",
        text: "Dacă valoarea rezervării eligibile este mai mică decât valoarea cardului, diferența neutilizată nu se reportează, nu se restituie și nu poate fi folosită pentru o altă rezervare.",
      },
      {
        num: "6.6",
        text: "Pentru aceeași rezervare poate fi utilizat un singur card cadou, dacă Travel Time nu confirmă în scris o excepție.",
      },
    ],
  },
  {
    id: "codul-unic",
    number: "7",
    title: "Codul unic și validarea cardului",
    paragraphs: [
      { num: "7.1", text: "Fiecare card conține un cod unic de identificare." },
      {
        num: "7.2",
        text: "Cardul este considerat valid numai după verificarea și confirmarea codului de către Travel Time.",
      },
      {
        num: "7.3",
        text: "Beneficiarul are obligația să păstreze confidențial codul și să protejeze cardul împotriva copierii, pierderii sau utilizării neautorizate.",
      },
      {
        num: "7.4",
        text: "Travel Time nu răspunde pentru utilizarea cardului de către o altă persoană care a obținut codul fără autorizarea beneficiarului.",
      },
      {
        num: "7.5",
        text: "Un cod utilizat, expirat, anulat, copiat, modificat, ilizibil sau obținut prin mijloace neautorizate va fi refuzat.",
      },
    ],
  },
  {
    id: "valabilitatea",
    number: "8",
    title: "Valabilitatea cardului",
    paragraphs: [
      {
        num: "8.1",
        text: "Cardul este valabil până la data înscrisă pe acesta sau menționată în comunicarea oficială prin care a fost transmis beneficiarului.",
      },
      {
        num: "8.2",
        text: "Rezervarea trebuie solicitată și confirmată în perioada de valabilitate a cardului.",
      },
      {
        num: "8.3",
        text: "Călătoria poate avea loc după data expirării cardului, dacă rezervarea a fost confirmată în perioada de valabilitate și dacă nu au fost comunicate alte condiții specifice.",
      },
      {
        num: "8.4",
        text: "După expirarea perioadei de valabilitate, cardul nu mai poate fi utilizat, iar valoarea acestuia nu se restituie și nu se prelungește automat.",
      },
    ],
  },
  {
    id: "restrictii",
    number: "9",
    title: "Restricții privind utilizarea",
    paragraphs: [
      {
        num: "9.1",
        text: "Cardul nu poate fi:",
        list: [
          "preschimbat în numerar;",
          "rambursat beneficiarului;",
          "utilizat pentru achitarea unor servicii deja rezervate;",
          "utilizat pentru plata avansurilor sau soldurilor aferente unor rezervări anterioare;",
          "revândut sau utilizat în scopuri comerciale;",
          "înlocuit în cazul pierderii, furtului, deteriorării sau divulgării codului unic, dacă acesta a fost deja utilizat.",
        ],
      },
      {
        num: "9.2",
        text: "Cardul poate fi utilizat numai de beneficiarul căruia i-a fost acordat, cu excepția cazului în care Travel Time aprobă în scris transferul către o altă persoană.",
      },
    ],
  },
  {
    id: "cumularea",
    number: "10",
    title: "Cumularea cu alte beneficii",
    paragraphs: [
      {
        num: "10.1",
        text: "Cardul nu se cumulează cu alte carduri cadou, vouchere, promoții, reduceri sau beneficii comerciale oferite de Travel Time ori de partenerii săi, dacă nu este comunicat în mod expres contrariul.",
      },
      {
        num: "10.2",
        text: "Cardul poate fi aplicat numai ofertelor și produselor declarate eligibile de Travel Time.",
      },
      {
        num: "10.3",
        text: "Eventualele reduceri acordate direct de furnizorii serviciilor turistice nu determină automat acceptarea cumulării acestora cu beneficiul oferit prin card.",
      },
    ],
  },
  {
    id: "modificarea-rezervarii",
    number: "11",
    title: "Modificarea sau anularea rezervării",
    paragraphs: [
      {
        num: "11.1",
        text: "După utilizarea cardului, orice modificare sau anulare a rezervării se efectuează în conformitate cu contractul privind pachetul de servicii de călătorie și cu regulile tarifare și condițiile furnizorilor implicați.",
      },
      {
        num: "11.2",
        text: "În cazul anulării rezervării de către beneficiar, valoarea cardului utilizat nu se rambursează în numerar și nu se reactivează automat.",
      },
      {
        num: "11.3",
        text: "Dacă rezervarea este anulată și există dreptul la restituirea unor sume, rambursarea se calculează exclusiv în limita sumelor efectiv achitate de beneficiar, după deducerea penalităților și a celorlalte costuri aplicabile. Valoarea promoțională a cardului nu este considerată sumă achitată de beneficiar.",
      },
      {
        num: "11.4",
        text: "În cazul modificării rezervării, beneficiul poate fi menținut numai dacă rezervarea modificată continuă să îndeplinească toate condițiile campaniei.",
      },
    ],
  },
  {
    id: "disponibilitatea",
    number: "12",
    title: "Disponibilitatea serviciilor",
    paragraphs: [
      {
        num: "12.1",
        text: "Deținerea cardului nu garantează disponibilitatea unei anumite destinații, unități de cazare, perioade de călătorie, categorii de cameră, curse aeriene sau a unui anumit tarif.",
      },
      {
        num: "12.2",
        text: "Rezervarea devine fermă numai după confirmarea serviciilor de către furnizori, acceptarea documentelor contractuale și achitarea sumelor datorate conform condițiilor comunicate de Travel Time.",
      },
      {
        num: "12.3",
        text: "Prețurile și disponibilitatea serviciilor turistice pot suferi modificări până la confirmarea fermă a rezervării.",
      },
    ],
  },
  {
    id: "refuzul",
    number: "13",
    title: "Refuzul sau anularea cardului",
    paragraphs: [
      {
        num: "13.1",
        text: "Travel Time poate refuza sau anula utilizarea unui card în cazul în care există suspiciuni rezonabile privind fraudarea campaniei, copierea sau modificarea cardului, transmiterea neautorizată a codului, furnizarea unor informații incorecte ori nerespectarea prezentelor condiții.",
      },
      {
        num: "13.2",
        text: "Travel Time își rezervă dreptul de a solicita informații suplimentare pentru verificarea identității beneficiarului și a modalității în care acesta a primit cardul.",
      },
    ],
  },
  {
    id: "modificarea-campaniei",
    number: "14",
    title: "Modificarea campaniei",
    paragraphs: [
      {
        num: "14.1",
        text: "Travel Time poate modifica, suspenda sau înceta campania în cazul apariției unor împrejurări care împiedică desfășurarea acesteia în condiții normale.",
      },
      {
        num: "14.2",
        text: "Orice modificare va fi comunicată prin canalele utilizate pentru promovarea campaniei și nu va afecta rezervările deja confirmate, cu excepția situațiilor impuse de lege sau determinate de cauze independente de Travel Time.",
      },
    ],
  },
  {
    id: "date-personale",
    number: "15",
    title: "Prelucrarea datelor personale",
    paragraphs: [
      {
        num: "15.1",
        text: "Datele beneficiarilor vor fi prelucrate de Travel Time în scopul administrării campaniei, verificării eligibilității, validării cardului, efectuării rezervării și îndeplinirii obligațiilor contractuale și legale.",
      },
      {
        num: "15.2",
        text: "Informațiile privind prelucrarea datelor cu caracter personal sunt disponibile în politica de confidențialitate a Travel Time.",
      },
    ],
  },
  {
    id: "reclamatii",
    number: "16",
    title: "Reclamații și soluționarea neînțelegerilor",
    paragraphs: [
      {
        num: "16.1",
        text: "Orice solicitare sau reclamație referitoare la card ori la desfășurarea campaniei poate fi transmisă Travel Time folosind datele de contact comunicate beneficiarului.",
      },
      {
        num: "16.2",
        text: "Eventualele neînțelegeri vor fi soluționate cu prioritate pe cale amiabilă, în conformitate cu documentele campaniei și cu legislația aplicabilă.",
      },
    ],
  },
  {
    id: "acceptarea",
    number: "17",
    title: "Acceptarea condițiilor",
    paragraphs: [
      {
        num: "17.1",
        text: "Primirea, prezentarea sau utilizarea cardului presupune cunoașterea și acceptarea integrală a prezentelor termene și condiții.",
      },
      {
        num: "17.2",
        text: "Beneficiarul are responsabilitatea de a consulta condițiile campaniei înainte de solicitarea rezervării.",
      },
      {
        num: "17.3",
        text: "În situațiile care nu sunt reglementate expres în prezentul document, eligibilitatea și modalitatea de utilizare a cardului vor fi stabilite de Travel Time, cu respectarea legislației aplicabile.",
      },
    ],
  },
];

// const KEY_POINTS = [
//   "Se folosește o singură dată, integral, pentru o singură rezervare nouă.",
//   "Nu se preschimbă în numerar și nu se rambursează.",
//   "Nu se cumulează cu alte vouchere sau reduceri, decât dacă Travel Time confirmă altfel.",
//   "Codul unic trebuie comunicat înainte de confirmarea și plata rezervării.",
// ];

function useGoogleFonts() {
  useEffect(() => {
    const id = "tt-terms-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";
    document.head.appendChild(link);
  }, []);
}

function TicketDivider() {
  return (
    <Flex align="center" px={{ base: 5, md: 10 }} bg={COLORS.tealDark}>
      <Box
        flex="1"
        borderBottom="2px dashed"
        borderColor="rgba(247,244,236,0.35)"
        h="0"
      />
    </Flex>
  );
}

function NavList({ activeId, onNavigate }) {
  return (
    <List spacing={0}>
      {SECTIONS.map((s) => {
        const active = s.id === activeId;
        return (
          <ListItem key={s.id}>
            <Link
              href={`#${s.id}`}
              onClick={(e) => onNavigate && onNavigate(e, s.id)}
              display="flex"
              alignItems="baseline"
              gap="10px"
              py="9px"
              pl="14px"
              borderLeft="2px solid"
              borderColor={active ? COLORS.gold : "transparent"}
              _hover={{ textDecoration: "none", borderColor: COLORS.gold }}
              transition="border-color 0.15s ease"
            >
              <Text
                as="span"
                fontFamily={FONT_MONO}
                fontSize="12px"
                color={active ? COLORS.gold : COLORS.inkMuted}
                minW="20px"
              >
                {s.number}
              </Text>
              <Text
                as="span"
                fontFamily={FONT_BODY}
                fontSize="14px"
                color={active ? COLORS.ink : COLORS.inkMuted}
                fontWeight={active ? 600 : 400}
              >
                {s.title}
              </Text>
            </Link>
          </ListItem>
        );
      })}
    </List>
  );
}

export default function TravelTimeTermeni() {
  useGoogleFonts();

  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const sectionRefs = useRef({});
  const { isOpen, onOpen, onClose } = useDisclosure();

  const sectionIds = useMemo(() => SECTIONS.map((s) => s.id), []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 },
    );

    sectionIds.forEach((id) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sectionIds]);

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    onClose();
  };

  return (
    <Box bg={COLORS.bg} minH="100%" fontFamily={FONT_BODY} color={COLORS.ink}>
      {/* Header — bandă tip „bilet de călătorie” */}
      <Box bg={COLORS.teal} color={COLORS.bg}>
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "flex-end" }}
          maxW="1100px"
          mx="auto"
          px={{ base: 5, md: 10 }}
          pt={{ base: 8, md: 12 }}
          pb={{ base: 6, md: 8 }}
          gap={3}
        >
          <Box>
            <Text
              fontFamily={FONT_MONO}
              fontSize="12px"
              letterSpacing="0.02em"
              color="rgba(247,244,236,0.7)"
              mb={2}
            >
              Travel Time D&R SRL
            </Text>
            <Heading
              as="h1"
              fontFamily={FONT_HEADING}
              fontWeight={500}
              fontSize={{ base: "32px", md: "44px" }}
              lineHeight="1.1"
              maxW="560px"
            >
              Card Cadou Travel Time
            </Heading>
          </Box>
          <Text
            fontFamily={FONT_MONO}
            fontSize="13px"
            color="rgba(247,244,236,0.8)"
            whiteSpace="nowrap"
          >
            Termeni și condiții
          </Text>
        </Flex>
      </Box>
      <TicketDivider />

      {/* Buton „Cuprins” — vizibil doar pe mobil/tabletă */}
      <Flex
        display={{ base: "flex", lg: "none" }}
        justify="space-between"
        align="center"
        px={5}
        py={3}
        borderBottom="1px solid"
        borderColor={COLORS.hairline}
        position="sticky"
        top={0}
        bg={COLORS.bg}
        zIndex={2}
      >
        <Text fontFamily={FONT_MONO} fontSize="12px" color={COLORS.inkMuted}>
          Secțiunea {SECTIONS.find((s) => s.id === activeId)?.number} din{" "}
          {SECTIONS.length}
        </Text>
        <Box
          as="button"
          onClick={onOpen}
          fontFamily={FONT_BODY}
          fontSize="14px"
          fontWeight={600}
          color={COLORS.teal}
          borderBottom="1px solid"
          borderColor={COLORS.teal}
          pb="1px"
        >
          Cuprins
        </Box>
      </Flex>

      <Flex
        maxW="1100px"
        mx="auto"
        align="flex-start"
        px={{ base: 5, md: 10 }}
        py={{ base: 8, md: 12 }}
        gap={{ base: 0, lg: 12 }}
      >
        {/* Navigare laterală — desktop */}
        <Box
          as="nav"
          display={{ base: "none", lg: "block" }}
          w="240px"
          flexShrink={0}
          position="sticky"
          top="32px"
        >
          <Text
            fontFamily={FONT_MONO}
            fontSize="11px"
            letterSpacing="0.03em"
            color={COLORS.inkMuted}
            mb={3}
          >
            Cuprins
          </Text>
          <NavList activeId={activeId} onNavigate={scrollToSection} />
        </Box>

        {/* Conținut principal */}
        <Box flex="1" maxW="680px">
          {/* <Box
            bg={COLORS.panel}
            border="1px solid"
            borderColor={COLORS.hairline}
            borderLeft="3px solid"
            borderLeftColor={COLORS.gold}
            px={{ base: 5, md: 6 }}
            py={5}
            mb={{ base: 8, md: 10 }}
          >
            <Text
              fontFamily={FONT_MONO}
              fontSize="11px"
              letterSpacing="0.03em"
              color={COLORS.gold}
              mb={3}
            >
              Pe scurt
            </Text>
            <List spacing={2}>
              {KEY_POINTS.map((point, i) => (
                <ListItem
                  key={i}
                  fontSize="15px"
                  lineHeight="1.6"
                  color={COLORS.ink}
                  display="flex"
                  gap="10px"
                >
                  <Text as="span" color={COLORS.gold}>
                    —
                  </Text>
                  <Text as="span">{point}</Text>
                </ListItem>
              ))}
            </List>
          </Box> */}

          {SECTIONS.map((section, idx) => (
            <Box
              as="section"
              key={section.id}
              id={section.id}
              ref={(el) => (sectionRefs.current[section.id] = el)}
              pt={idx === 0 ? 0 : 8}
              pb={8}
              scrollMarginTop="24px"
            >
              <Flex align="baseline" gap="14px" mb={4}>
                <Text
                  fontFamily={FONT_MONO}
                  fontSize="14px"
                  color={COLORS.gold}
                >
                  {section.number}
                </Text>
                <Heading
                  as="h2"
                  fontFamily={FONT_HEADING}
                  fontWeight={500}
                  fontSize={{ base: "20px", md: "23px" }}
                  color={COLORS.ink}
                >
                  {section.title}
                </Heading>
              </Flex>

              {section.paragraphs.map((p) => (
                <Box key={p.num} mb={3.5}>
                  <Text fontSize="15.5px" lineHeight="1.75" color={COLORS.ink}>
                    <Text
                      as="span"
                      fontFamily={FONT_MONO}
                      fontSize="12px"
                      color={COLORS.inkMuted}
                      mr="8px"
                    >
                      {p.num}
                    </Text>
                    {p.text}
                  </Text>
                  {p.list && (
                    <List spacing={1.5} mt={2} pl="34px">
                      {p.list.map((item, i) => (
                        <ListItem
                          key={i}
                          fontSize="15px"
                          lineHeight="1.7"
                          color={COLORS.ink}
                          display="flex"
                          gap="8px"
                        >
                          <Text
                            as="span"
                            fontFamily={FONT_MONO}
                            fontSize="12px"
                            color={COLORS.inkMuted}
                          >
                            {String.fromCharCode(97 + i)})
                          </Text>
                          <Text as="span">{item}</Text>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              ))}

              {idx < SECTIONS.length - 1 && (
                <Divider borderColor={COLORS.hairline} mt={4} />
              )}
            </Box>
          ))}

          <Box
            mt={4}
            pt={6}
            borderTop="1px solid"
            borderColor={COLORS.hairline}
          >
            <Text fontSize="13px" color={COLORS.inkMuted} lineHeight="1.7">
              Acest document reglementează campania promoțională „Card Cadou
              Travel Time”, organizată de Travel Time D&R SRL. Pentru întrebări
              legate de un card primit, foloseşte datele de contact comunicate
              odată cu acesta.
            </Text>
          </Box>
        </Box>
      </Flex>

      {/* Drawer cu cuprins — mobil/tabletă */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent bg={COLORS.bg}>
          <DrawerCloseButton color={COLORS.ink} />
          <DrawerBody pt={12}>
            <Text
              fontFamily={FONT_MONO}
              fontSize="11px"
              letterSpacing="0.03em"
              color={COLORS.inkMuted}
              mb={3}
            >
              Cuprins
            </Text>
            <NavList activeId={activeId} onNavigate={scrollToSection} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
